/**
 * Retrieves the selected pricing details based on the provided payment information, listing ID, and deal type.
 *
 * @param {Object} payments - The payment details object.
 * @param {string} payments.dealType - The type of deal (e.g., 'finance', 'lease', 'cash').
 * @param {Object} [payments.listingPayments] - Payment details for listings.
 * @param {Object} [payments.penciling] - Penciling details, including offer price and custom lender information.
 * @param {Object} [payments.listingLeasePayments] - Lease payment details for listings.
 * @param {Object} [payments.listingCashPayments] - Cash payment details for listings.
 * @param {string} listingId - The ID of the listing for which pricing details are being retrieved.
 * @param {Object} pricingDetail - Fallback pricing details from the monolith.
 * @param {number|null} [cashDealPrice=null] - Optional cash deal price.
 * @returns {Object} An object containing the selected pricing details.
 * @returns {Object} return.paymentDetails - The payment details for the selected deal type.
 * @returns {number|undefined} return.downPayment - The down payment amount, if applicable.
 * @returns {Object} return.selectedPricingDetails - The selected pricing details, either from PS or the fallback.
 * @returns {number|string} return.price - The final price, determined by offer price, sale price, or MSRP.
 */
const getSelectedPricingDetails = function (payments, listingId, pricingDetail) {
  let cashDealPrice = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : null;
  const {
    dealType,
    listingPayments = {},
    penciling = {},
    listingLeasePayments = {},
    listingCashPayments = {}
  } = payments;
  const {
    offerPrice = '',
    pencilingPayments,
    icoTradeInOffer = '',
    customLender = {}
  } = penciling;
  const pencilingPaymentsDetails = pencilingPayments?.[listingId];
  const listingPaymentDetails = !pencilingPaymentsDetails && listingPayments?.[listingId];
  const listingLeasePaymentDetails = listingLeasePayments?.[listingId];
  const listingCashPaymentDetails = listingCashPayments?.[listingId];
  let paymentDetails = {
    offerPrice,
    icoTradeInOffer,
    customLender,
    cashDealPrice
  };
  let pricingDetails;
  let downPayment;
  switch (dealType) {
    case 'finance':
      if (pencilingPaymentsDetails) {
        paymentDetails = Object.assign(paymentDetails, {
          ...pencilingPaymentsDetails
        });
        pricingDetails = pencilingPaymentsDetails?.pricingDetails;
        downPayment = pencilingPaymentsDetails?.downPayment;
      } else if (listingPaymentDetails) {
        paymentDetails = Object.assign(paymentDetails, {
          ...listingPaymentDetails
        });
        pricingDetails = listingPaymentDetails?.pricingDetails;
        downPayment = listingPaymentDetails?.downPayment;
      }
      break;
    case 'lease':
      if (listingLeasePaymentDetails) {
        paymentDetails = Object.assign(paymentDetails, {
          ...listingLeasePaymentDetails
        });
        pricingDetails = listingLeasePaymentDetails?.pricingDetails;
        downPayment = listingLeasePaymentDetails?.downPayment;
      }
      break;
    case 'cash':
      if (listingCashPaymentDetails) {
        paymentDetails = Object.assign(paymentDetails, {
          ...listingCashPaymentDetails
        });
        pricingDetails = listingCashPaymentDetails?.pricingDetails;
        downPayment = listingCashPaymentDetails?.downPayment;
      }
      break;
  }
  const selectedPricingDetails = pricingDetails || pricingDetail; // 'pricingDetails' from PS and 'pricingDetail' from monolith(fallBack)
  const {
    salePrice,
    msrp
  } = selectedPricingDetails;
  const price = offerPrice || salePrice || msrp;
  return {
    paymentDetails,
    downPayment,
    selectedPricingDetails,
    price
  };
};
export { getSelectedPricingDetails as default };
